/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { ReactElement, useState, useRef, useEffect } from 'react';
import { FieldProps, ErrorMessage, useFormikContext, getIn } from 'formik';
import classnames from 'classnames';
import { tile } from '@interfaces/interfaces';
import { Alert, Button, Modal, TextInput } from '@components';
import InfoIcon from '../../images/info.svg';
import TickIcon from '../../images/tickIcon.svg';
import DownArrow from '../../images/downArrow.svg';
import styles from './TileRadioAccessibleWithInfo.module.scss';

interface iTileRadioInfo {
  caption: string;
  tileValue?: string;
}

interface iProps extends FieldProps {
  title?: string;
  isMandatory: boolean;
  tileData: tile[];
  orientationRow: boolean;
  mobileCollapseRow?: boolean;
  classname?: string;
  wrapTiles?: boolean;
  info?: boolean;
  tileChanged?: () => void;
  alwaysOpen?: boolean;
  hasBeenNominatedBefore?: boolean;
  validateFieldName?: string;
}
interface NominationAccordionProps {
  itemOption: string;
}
const TileRadioAccessibleWithInfo = ({ title, isMandatory, field, form, tileData, orientationRow, mobileCollapseRow = true, classname = '', wrapTiles = false, info, tileChanged, alwaysOpen = false, hasBeenNominatedBefore = false, validateFieldName = '' }: iProps): ReactElement => {
  const { errors, touched, values }: { errors: any; touched: any, values: any } = useFormikContext();
  const formikContext = useFormikContext();
  const hasError = getIn(errors, field.name);
  const touchedError = getIn(touched, field.name);
  const tileLength = tileData.length;
  const [accordionOpen, setAccordionOpen] = useState<string | null>(values.selectTheOption);
  const prevAccordionOpenRef = useRef<string | null>(null);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [prevSelect, setPrevSelect] = useState<string>('');
  const prevAccordionOpen = prevAccordionOpenRef.current;
  // Create a refs object
  const refs: { [key: string]: React.RefObject<HTMLDivElement | null> } = tileData.reduce((acc: { [key: string]: React.RefObject<HTMLDivElement | null> }, tileItem, index) => {
    acc[`${field.name}${index}`] = React.createRef();
    return acc;
  }, {});
  const calculateHeight = (tileItemValue: string) => {
    const tileItemValueA = tileItemValue === accordionOpen && tileItemValue !== values.closeTheOption ? `${heights[tileItemValue]}px` : '0';
    return tileItemValueA;
  };
  let iconDivContent: React.ReactElement;
  const returnIconContent = (currentSelected: string, index: number): React.ReactElement => {
    if (prevSelect === currentSelected && values.selectTheOption && values.selectTheOption !== currentSelected) { // fix this
      iconDivContent = (
        <div className={styles.iconDiv}>
          <img src={DownArrow} className={`${styles.iconChanged} ${styles.arrowIcon}`} alt="arrow" />
        </div>
      );
    } else if (values.selectTheOption && values.selectTheOption === currentSelected) {
      iconDivContent = (
        <div className={styles.iconDiv}>
          <img src={TickIcon} className={styles.tickIcon} alt="tick" />
        </div>
      );
    } else {
      iconDivContent = (
        <div className={styles.iconDiv}>
          <img
            src={DownArrow}
            className={`${accordionOpen === (field.name + index) && accordionOpen !== values.closeTheOption ? styles.iconClicked : styles.iconInitiated} ${styles.arrowIcon}`}
            alt="Expand/Contract the question" />
        </div>
      );
    }
    return iconDivContent;
  };
  const getBrowser = () => {
    const { userAgent } = navigator;
    let browser = 'unknown';
    if (/chrome/i.test(userAgent)) {
      browser = 'chrome';
    } else if (/safari/i.test(userAgent)) {
      browser = 'safari';
    }
    return browser;
  };

  useEffect(() => {
    if (accordionOpen) {
      // Now that the accordion content is visible, calculate its height
      const height = refs[accordionOpen].current?.getBoundingClientRect().height || 0;
      setHeights((prevHeights) => ({
        ...prevHeights,
        [accordionOpen]: height,
      }));
    } else {
      setHeights({});
    }
    if (values.selectTheOption !== prevSelect) {
      setPrevSelect(values.selectTheOption);
    }
    prevAccordionOpenRef.current = accordionOpen;
  }, [accordionOpen, values.closeTheOption, values.selectTheOption, field.value]);

  return (
    <>
      {title && (
        <div
          className={classnames({
            [styles.title]: true,
            [styles.titleError]: hasError && touchedError,
          })}>
          {title?.includes('\n\r')
            ? (
              <span className={styles.titleBold}>
                {title.split('\n\r')[0]}
                <br />
                <span style={{ fontWeight: 'normal' }}>{title.split('\n\r')[1]}</span>
              </span>
            )
            : <span className={styles.titleBold}>{title}</span>}
          {!isMandatory && <span className={styles.optional}> ( optional )</span>}
        </div>
      )}
      <div
        className={classnames({
          [styles.tileContainer]: true,
          [styles.wrapTiles]: wrapTiles,
          [styles.orientationRow]: orientationRow && !mobileCollapseRow,
          [styles.orientationRowCollapse]: orientationRow && mobileCollapseRow,
          [styles.orientationColumn]: !orientationRow,
          [styles.tileItemExpand]: tileLength > 2,
        })}
        role="radiogroup"
      >
        {tileData
          .map((tileItem, index) => (
            <div
              key={tileItem.value}
              className={classnames({
              [styles.hidden]: (!hasBeenNominatedBefore && index === 3),
            })}
          >
              <div
                key={tileItem.value}
                tabIndex={0}
                aria-checked="false"
                role="radio"
                className={classnames({
                  [styles.tileItem]: true,
                  [styles.selected]: calculateHeight(`${field.name}${index}`) !== '0' && field.value === tileItem.value,
                })}>
                <input
                  type="radio"
                  id={`${field.name}${tileItem.value}`}
                  name={field.name}
                  value={tileItem.value}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      form.setFieldTouched(field.name);
                      form.setFieldValue(field.name, tileItem.value);
                      // handle tileChanged only a tile is already selected and a new tile is now selected
                      if (getIn(values, field.name) && tileItem.value !== getIn(values, field.name) && tileChanged) tileChanged();
                    }
                  }}
                  onFocus={() => setAccordionOpen(`${field.name}${index}`)}
                  onClick={(event) => {
                    formikContext.setFieldValue('closeTheOption', '');
                    if (getBrowser() === 'safari') {
                      setAccordionOpen((previousAccordionOpen) => {
                        if (previousAccordionOpen === `${field.name}${index}`) {
                          // If the accordion is already open, close it
                          return null;
                        }
                          // If the accordion is closed, open it
                          return `${field.name}${index}`;
                      });
                    } else {
                      // eslint-disable-next-line no-lonely-if
                      if (accordionOpen === prevAccordionOpen) {
                        // If the accordion is already open, close it
                        setAccordionOpen(null);
                      } else {
                        // If the accordion is closed, open it
                        setAccordionOpen(`${field.name}${index}`);
                      }
                    }
                  }}
                  onChange={() => {
                    form.setFieldTouched(field.name);
                    form.setFieldValue(field.name, tileItem.value);
                    // handle tileChanged only a tile is already selected and a new tile is now selected
                    if (getIn(values, field.name) && tileItem.value !== getIn(values, field.name) && tileChanged) tileChanged();
                  }}
                />
                <label htmlFor={`${field.name}${tileItem.value}`}>
                  <div
                    className={classnames({
                      [styles.tile]: true,
                      [`${classname}`]: true,
                    })}>
                    <span className={styles.tileValue}>{tileItem.value}</span>
                    {returnIconContent(`${field.name}${index}`, index)}
                  </div>
                </label>
              </div>
              <div className={styles.accordionContent} style={{ height: calculateHeight(`${field.name}${index}`) }}>
                <div
                  key={`${tileItem.value}.info`}
                  ref={refs[`${field.name}${index}`]}
                  className={styles.divPadding}>
                  <div className={styles.accordContent}>{tileItem.body}</div>
                </div>
              </div>
            </div>
          ))}
      </div>
      <div>
        <ErrorMessage className={styles.errorMsg} name={validateFieldName || field.name} component="span" />
      </div>
    </>
  );
};

export default React.memo(TileRadioAccessibleWithInfo);
